// v10/src/utils/speechHelper.js

export const VOICE_MODES = {
  SYNTHETIC:   'synthetic',
  ELEVENLABS:  'elevenlabs',
  CUSTOM:      'custom',
  DISABLED:    'disabled',
};

let currentAudio      = null;
let currentVoiceMode  = VOICE_MODES.ELEVENLABS;

// Callback pendiente del audio actual. Se setea al iniciar un Audio y se
// dispara automáticamente cuando termina (onended) o cuando se interrumpe
// por un nuevo audio (vía _flushPendingOnEnd). Sin esto, los callers que
// esperan un onEnd quedan colgados cuando otro audio interrumpe al anterior.
let currentAudioOnEnd = null;

// Helper interno: si hay un onEnd pendiente del audio anterior, dispararlo
// ANTES de matarlo, para que el caller no quede esperando un callback que
// nunca llega. Luego para el audio actual.
// IMPORTANTE: solo lo usan las funciones internas (playCustomAudio, playFromUrl,
// speakTextElevenLabs, speakPreLandingStep) que reemplazan un audio por otro.
// La función exportada stopSpeech() mantiene su comportamiento original (no
// dispatcha onEnd) para no romper a callers externos como VoiceAssistant.
const _flushAndStop = () => {
  window.speechSynthesis.cancel();
  if (currentAudio) {
    const pending = currentAudioOnEnd;
    currentAudioOnEnd = null;
    try { currentAudio.pause(); currentAudio.currentTime = 0; } catch (_) {}
    currentAudio = null;
    if (pending) {
      try { pending(); } catch (e) { console.error('[Speech] onEnd dispatch:', e); }
    }
  }
};

const toSlug = (text) =>
  text.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '').trim()
    .replace(/\s+/g, '-');

export const setVoiceMode = (mode) => {
  if (Object.values(VOICE_MODES).includes(mode)) {
    currentVoiceMode = mode;
    localStorage.setItem('condor360_voice_mode', mode);
  }
};

export const getVoiceMode = () =>
  localStorage.getItem('condor360_voice_mode') || VOICE_MODES.ELEVENLABS;

export const initVoiceMode = () => {
  currentVoiceMode = getVoiceMode();
};

// ── Helper: idioma actual de la app ──────────────────────────────
const getLanguage = () => {
  const lang = localStorage.getItem('condor360_language') || 'es-AR';
  return lang === 'en' ? 'en' : 'es';
};

// ── Helper: variante de voz (valentina=ES default, lauren=EN) ──────
const getVoiceVariant = (language) => {
  if (language === 'en') return 'lauren';
  const lang = localStorage.getItem('condor360_language') || 'es-AR';
  return 'valentina'; // ES default → Valentina
};

export const setVoiceVariant = (variant) => {
  localStorage.setItem('condor360_voice_variant', variant);
};

const MANIFEST_KEY = (aircraftId) => `condor360_audio_manifest_${aircraftId}`;

// ── IndexedDB — store unificado para checklist y pre-aterrizaje ──
const IDB_NAME    = 'condor360-audio-db';
const IDB_STORE   = 'audio';
const IDB_VERSION = 1;

const openAudioDB = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(IDB_NAME, IDB_VERSION);
  req.onupgradeneeded = (e) => {
    const db = e.target.result;
    if (!db.objectStoreNames.contains(IDB_STORE)) {
      db.createObjectStore(IDB_STORE, { keyPath: 'key' });
    }
  };
  req.onsuccess = (e) => resolve(e.target.result);
  req.onerror   = (e) => reject(e.target.error);
});

const bufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary  = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return btoa(binary);
};

const base64ToAudioUrl = (base64) => {
  const binary = atob(base64);
  const bytes  = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'audio/mpeg' });
  return URL.createObjectURL(blob);
};

const idbSave = async (key, url) => {
  try {
    const db = await openAudioDB();
    const exists = await new Promise((resolve) => {
      const tx  = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = (e) => resolve(!!e.target.result);
      req.onerror   = () => resolve(false);
    });
    if (exists) return true;
    const response = await fetch(url);
    if (!response.ok) return false;
    const buffer = await response.arrayBuffer();
    const base64 = bufferToBase64(buffer);
    return new Promise((resolve) => {
      const tx  = db.transaction(IDB_STORE, 'readwrite');
      const req = tx.objectStore(IDB_STORE).put({ key, data: base64, cachedAt: Date.now() });
      req.onsuccess = () => resolve(true);
      req.onerror   = () => resolve(false);
    });
  } catch { return false; }
};

const idbGet = async (key) => {
  try {
    const db = await openAudioDB();
    return new Promise((resolve) => {
      const tx  = db.transaction(IDB_STORE, 'readonly');
      const req = tx.objectStore(IDB_STORE).get(key);
      req.onsuccess = (e) => {
        const record = e.target.result;
        if (!record) { resolve(null); return; }
        try { resolve(base64ToAudioUrl(record.data)); }
        catch { resolve(null); }
      };
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
};

// ── Checklist audio ───────────────────────────────────────────────
const checklistIdbKey = (sectionName, text) => {
  const lang    = localStorage.getItem('condor360_language') || 'es-AR';
  const variant = lang === 'en' ? 'lauren' : 'valentina';
  return `checklist__${variant}__${toSlug(sectionName)}__${toSlug(text)}`;
};

const getChecklistCachedAudio = async (sectionName, text) =>
  idbGet(checklistIdbKey(sectionName, text));

const cacheAudioToIDB = async (url, sectionName, text) =>
  idbSave(checklistIdbKey(sectionName, text), url);

export const fetchAndStoreManifest = async (aircraftId) => {
  if (!aircraftId) return;
  try {
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const token   = getAuthToken();
    const res     = await fetch(`${apiBase}/api/tts/cache/${aircraftId}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) return;
    const data = await res.json();
    if (data.manifest && Object.keys(data.manifest).length > 0) {
      localStorage.setItem(MANIFEST_KEY(aircraftId), JSON.stringify({
        manifest:  data.manifest,
        updatedAt: data.updatedAt,
        fetchedAt: new Date().toISOString(),
      }));
      const entries = Object.entries(data.manifest);
      console.log(`[AudioCache] Manifiesto cargado para ${aircraftId}: ${entries.length} ítems — pre-descargando blobs...`);

      if (navigator.onLine) {
        let cached = 0;
        for (const [key, url] of entries) {
          const parts = key.split('/');
          if (parts.length === 2) {
            const ok = await cacheAudioToIDB(url, parts[0], parts[1]);
            if (ok) cached++;
          }
          await new Promise(r => setTimeout(r, 50));
        }
        console.log(`[AudioCache] Pre-descarga completada: ${cached}/${entries.length} audios en IndexedDB`);
      }
    }
  } catch (err) {
    console.warn('[AudioCache] No se pudo cargar el manifiesto:', err.message);
  }
};

const getCachedAudioUrl = (aircraftId, sectionName, text) => {
  if (!aircraftId || !sectionName || !text) return null;
  try {
    const stored = localStorage.getItem(MANIFEST_KEY(aircraftId));
    if (!stored) return null;
    const { manifest } = JSON.parse(stored);
    const key = `${toSlug(sectionName)}/${toSlug(text)}`;
    return manifest?.[key] || null;
  } catch { return null; }
};

export const getAudioCacheInfo = async (aircraftId) => {
  try {
    const stored = localStorage.getItem(MANIFEST_KEY(aircraftId));
    if (!stored) return { cached: false, count: 0, offlineCount: 0, updatedAt: null };
    const { manifest, updatedAt, fetchedAt } = JSON.parse(stored);
    const count = Object.keys(manifest || {}).length;
    let offlineCount = 0;
    try {
      const db = await openAudioDB();
      offlineCount = await new Promise((resolve) => {
        const tx  = db.transaction(IDB_STORE, 'readonly');
        const req = tx.objectStore(IDB_STORE).count();
        req.onsuccess = (e) => resolve(e.target.result);
        req.onerror   = () => resolve(0);
      });
    } catch {}
    return { cached: true, count, offlineCount, updatedAt, fetchedAt };
  } catch { return { cached: false, count: 0, offlineCount: 0, updatedAt: null }; }
};

export const clearAudioCache = async (aircraftId) => {
  localStorage.removeItem(MANIFEST_KEY(aircraftId));
  try {
    const db = await openAudioDB();
    await new Promise((resolve) => {
      const tx  = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror    = () => resolve();
    });
  } catch {}
};

const getAuthToken = () => {
  try {
    const raw = localStorage.getItem('condor360_current_user')
             || sessionStorage.getItem('condor360_current_user');
    return JSON.parse(raw)?.token || '';
  } catch { return ''; }
};

const textToFilename = (text) =>
  text.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '').trim()
    .replace(/\s+/g, '-');

const getAudioPath = (aircraftId, sectionName, itemText) => {
  const aircraftSlug = aircraftId.toLowerCase().replace(/\s+/g, '-');
  return `/audio/${aircraftSlug}/${textToFilename(sectionName)}/${textToFilename(itemText)}.mp3`;
};

const audioFileExists = async (path) => {
  try {
    const r = await fetch(path, { method: 'HEAD' });
    return r.ok;
  } catch { return false; }
};

const playCustomAudio = (path, onEnd = () => {}) => {
  _flushAndStop(); // dispara onEnd pendiente del audio anterior antes de matarlo
  currentAudio = new Audio(path);
  currentAudio.volume = 1.0;
  currentAudioOnEnd   = onEnd; // tracking para que un futuro _flushAndStop lo dispare
  currentAudio.onended = () => {
    const cb = currentAudioOnEnd;
    currentAudio = null;
    currentAudioOnEnd = null;
    if (cb) cb();
  };
  currentAudio.onerror = () => {
    const fallbackText = path.split('/').pop().replace('.mp3', '').replace(/-/g, ' ');
    const cb = currentAudioOnEnd;
    currentAudio = null;
    currentAudioOnEnd = null;
    speakTextSynthetic(fallbackText, cb || (() => {}));
  };
  currentAudio.play().catch(() => {
    currentAudio = null;
    currentAudioOnEnd = null;
  });
};

const speakTextSynthetic = (text, onEnd = () => {}) => {
  const lang = getLanguage();
  const utterance    = new SpeechSynthesisUtterance(text);
  utterance.lang     = lang === 'en' ? 'en-US' : 'es-ES';
  utterance.rate     = 0.9;
  utterance.pitch    = 1;
  utterance.volume   = 1;
  utterance.onend    = onEnd;
  window.speechSynthesis.speak(utterance);
};

const playFromUrl = (url, text, onEnd = () => {}, finalFallback = null) => {
  _flushAndStop();
  let fallbackCalled = false;
  const fallback = (reason) => {
    if (fallbackCalled) return;
    fallbackCalled = true;
    console.warn(`[AudioCache] Fallback desde URL (${reason})`);
    currentAudio = null;
    currentAudioOnEnd = null;
    if (finalFallback) { finalFallback(); }
    else { speakTextSynthetic(text, onEnd); }
  };

  currentAudio = new Audio(url);
  currentAudio.volume = 1.0;
  currentAudioOnEnd   = onEnd;
  currentAudio.onended = () => {
    const cb = currentAudioOnEnd;
    currentAudio = null;
    currentAudioOnEnd = null;
    if (cb) cb();
  };
  currentAudio.onerror = () => fallback('onerror');
  currentAudio.play().catch(() => fallback('play rejected'));
};

const elevenLabsMemCache = new Map();

// ✅ Pasa el idioma actual al backend para seleccionar la voz correcta
const speakTextElevenLabs = async (text, onEnd = () => {}) => {
  const language = getLanguage();
  let fallbackCalled = false;
  const fallback = (reason) => {
    if (fallbackCalled) return;
    fallbackCalled = true;
    console.warn(`[TTS] ElevenLabs fallback a synthetic (${reason})`);
    speakTextSynthetic(text, onEnd);
  };

  try {
    const cacheKey = `${language}:${text}`;
    let audioUrl = elevenLabsMemCache.get(cacheKey);

    if (!audioUrl) {
      if (!navigator.onLine) {
        fallback('offline, sin cache en memoria');
        return;
      }

      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token   = getAuthToken();

      // ── Timeout de 8s con AbortController ─────────────────────────
      // Sin esto, si la API se cuelga la promesa queda viva eternamente y
      // el caller (App.js, FlowScreen, etc.) nunca recibe su onEnd. Eso es
      // la causa principal de "Valentina nunca habla esta frase".
      const controller = new AbortController();
      const timeoutId  = setTimeout(() => controller.abort(), 8000);

      let response;
      try {
        response = await fetch(`${apiBase}/api/tts/synthesize`, {
          method:  'POST',
          headers: {
            'Content-Type':  'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ text, language, voice_variant: getVoiceVariant(language) }),
          signal: controller.signal,
        });
      } finally {
        clearTimeout(timeoutId);
      }

      if (response.status === 402) {
        const data = await response.json();
        console.warn('[TTS] Límite de voz alcanzado:', data.message);
        speakTextSynthetic(`Valentina no disponible este mes. ${text}`, onEnd);
        return;
      }
      if (!response.ok) throw new Error(`TTS error: ${response.status}`);

      const blob = await response.blob();
      audioUrl   = URL.createObjectURL(blob);

      if (elevenLabsMemCache.size >= 50) {
        const firstKey = elevenLabsMemCache.keys().next().value;
        URL.revokeObjectURL(elevenLabsMemCache.get(firstKey));
        elevenLabsMemCache.delete(firstKey);
      }
      elevenLabsMemCache.set(cacheKey, audioUrl);
    }

    _flushAndStop();
    currentAudio        = new Audio(audioUrl);
    currentAudio.volume = 1.0;
    currentAudioOnEnd   = onEnd;
    currentAudio.onended = () => {
      const cb = currentAudioOnEnd;
      currentAudio = null;
      currentAudioOnEnd = null;
      if (cb) cb();
    };
    currentAudio.onerror = () => fallback('onerror');
    await currentAudio.play();

  } catch (err) {
    // AbortError = el fetch fue cancelado por timeout
    const reason = err.name === 'AbortError' ? 'timeout 8s' : err.message;
    fallback(reason);
  }
};

// ── Pre-aterrizaje: IndexedDB (mismo store que checklist) ────────
const preLandingIdbKey = (text) =>
  `prelanding__${toSlug(text)}`;

const getPreLandingCachedAudio = async (text) =>
  idbGet(preLandingIdbKey(text));

// ✅ Pasa el idioma al backend al cachear pre-aterrizaje
const cachePreLandingText = async (text) => {
  try {
    if (!navigator.onLine) return false;

    const existing = await getPreLandingCachedAudio(text);
    if (existing) { URL.revokeObjectURL(existing); return true; }

    const apiBase  = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    const token    = getAuthToken();
    const language = getLanguage();

    const response = await fetch(`${apiBase}/api/tts/synthesize`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ text, language, voice_variant: getVoiceVariant(language) }),
    });
    if (!response.ok) return false;

    const buffer = await response.arrayBuffer();
    const base64 = bufferToBase64(buffer);
    const db     = await openAudioDB();
    const key    = preLandingIdbKey(text);

    return new Promise((resolve) => {
      const tx  = db.transaction(IDB_STORE, 'readwrite');
      const req = tx.objectStore(IDB_STORE).put({ key, data: base64, cachedAt: Date.now() });
      req.onsuccess = () => { console.log(`[PreLanding] ✅ Cacheado: "${text.substring(0, 40)}..."`); resolve(true); };
      req.onerror   = () => resolve(false);
    });
  } catch (err) {
    console.warn(`[PreLanding] No se pudo cachear: ${err.message}`);
    return false;
  }
};

export const preCachePreLandingAudio = async (steps) => {
  if (!steps || !steps.length) return;
  if (!navigator.onLine) return;

  console.log('[PreLanding] Iniciando pre-cacheo silencioso de audios...');

  const results = [];
  for (const step of steps) {
    const result = await cachePreLandingText(step.voice).catch(() => false);
    results.push(result);
    await new Promise(r => setTimeout(r, 300));
  }

  const ok     = results.filter(r => r.status === 'fulfilled' && r.value).length;
  const failed = results.filter(r => r.status === 'rejected' || !r.value).length;
  console.log(`[PreLanding] Cache completado: ${ok} OK, ${failed} fallidos`);
};

export const speakPreLandingStep = async (text, onEnd = () => {}) => {
  if (currentVoiceMode === VOICE_MODES.DISABLED) { onEnd(); return; }

  if (currentVoiceMode === VOICE_MODES.ELEVENLABS) {
    const cachedUrl = await getPreLandingCachedAudio(text);
    if (cachedUrl) {
      console.log('[PreLanding] ✅ Reproduciendo desde cache offline');
      _flushAndStop();
      let fallbackCalled = false;
      const doFallback = () => {
        if (fallbackCalled) return;
        fallbackCalled = true;
        currentAudio = null;
        currentAudioOnEnd = null;
        speakTextSynthetic(text, onEnd);
      };
      currentAudio = new Audio(cachedUrl);
      currentAudio.volume = 1.0;
      currentAudioOnEnd   = onEnd;
      currentAudio.onended = () => {
        const cb = currentAudioOnEnd;
        currentAudio = null;
        currentAudioOnEnd = null;
        if (cb) cb();
      };
      currentAudio.onerror = doFallback;
      currentAudio.play().catch(doFallback);
      return;
    }

    await speakTextElevenLabs(text, onEnd);
    return;
  }

  speakTextSynthetic(text, onEnd);
};

// ✅ ElevenLabs only — pasa idioma. Silent on failure.
export const speakElevenLabsOnly = async (text, onEnd = () => {}) => {
  if (currentVoiceMode === VOICE_MODES.DISABLED) { onEnd(); return; }
  if (!navigator.onLine) { onEnd(); return; }
  const language = getLanguage();
  try {
    const cacheKey = `${language}:${text}`;
    let audioUrl = elevenLabsMemCache.get(cacheKey);
    if (!audioUrl) {
      const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const token   = getAuthToken();
      const response = await fetch(`${apiBase}/api/tts/synthesize`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ text, language, voice_variant: getVoiceVariant(language) }),
      });
      if (!response.ok) { onEnd(); return; }
      const blob = await response.blob();
      audioUrl   = URL.createObjectURL(blob);
      if (elevenLabsMemCache.size >= 50) {
        const firstKey = elevenLabsMemCache.keys().next().value;
        URL.revokeObjectURL(elevenLabsMemCache.get(firstKey));
        elevenLabsMemCache.delete(firstKey);
      }
      elevenLabsMemCache.set(cacheKey, audioUrl);
    }
    stopSpeech();
    currentAudio        = new Audio(audioUrl);
    currentAudio.volume = 1.0;
    currentAudio.onended = () => { onEnd(); currentAudio = null; };
    currentAudio.onerror = () => { onEnd(); currentAudio = null; };
    await currentAudio.play();
  } catch {
    onEnd();
  }
};

export const speakText = async (text, onEnd = () => {}, context = {}) => {
  if (currentVoiceMode === VOICE_MODES.DISABLED) { onEnd(); return; }

  if (currentVoiceMode === VOICE_MODES.ELEVENLABS) {
    if (context.sectionName) {
      const lookupText = context.itemText || text;

      // 1. IndexedDB (funciona offline)
      const localBlob = await getChecklistCachedAudio(context.sectionName, lookupText);
      if (localBlob) {
        console.log('[AudioCache] ✅ Reproduciendo desde IndexedDB:', toSlug(lookupText));
        playFromUrl(localBlob, text, onEnd, () => speakTextElevenLabs(text, onEnd));
        return;
      }

      // 2. URL remota del manifiesto (Cloudinary — requiere red)
      const remoteUrl = getCachedAudioUrl(context.aircraftId, context.sectionName, lookupText);
      if (remoteUrl) {
        console.log('[AudioCache] Intentando URL remota:', toSlug(lookupText));
        playFromUrl(remoteUrl, text, onEnd, () => speakTextElevenLabs(text, onEnd));
        return;
      }
    }
    // 3. ElevenLabs online (con idioma correcto)
    await speakTextElevenLabs(text, onEnd);
    return;
  }

  if (currentVoiceMode === VOICE_MODES.CUSTOM && context.aircraftId && context.sectionName) {
    const path   = getAudioPath(context.aircraftId, context.sectionName, text);
    const exists = await audioFileExists(path);
    if (exists) { playCustomAudio(path, onEnd); return; }
  }

  speakTextSynthetic(text, onEnd);
};

export const stopSpeech = () => {
  window.speechSynthesis.cancel();
  if (currentAudio) {
    try { currentAudio.pause(); currentAudio.currentTime = 0; } catch (_) {}
    currentAudio = null;
  }
  // Limpiar tracking de onEnd pendiente sin dispararlo: el caller externo
  // (típicamente VoiceAssistant tras detectar "listo") quiere PARAR la
  // cadena, no continuarla.
  currentAudioOnEnd = null;
};

export const preloadSectionAudio = async (aircraftId, sectionName, items) => {
  if (currentVoiceMode !== VOICE_MODES.CUSTOM) return;
  items.forEach(item => {
    const audio = new Audio();
    audio.preload = 'auto';
    audio.src = getAudioPath(aircraftId, sectionName, typeof item === 'string' ? item : item.text);
  });
};

export const getAudioStats = async (aircraftId, checklist) => {
  if (!checklist) return { total: 0, available: 0, missing: 0 };
  let total = 0, available = 0;
  for (const section of checklist) {
    for (const item of section.items) {
      const path = getAudioPath(aircraftId, section.section, typeof item === 'string' ? item : item.text);
      total++;
      if (await audioFileExists(path)) available++;
    }
  }
  return { total, available, missing: total - available, percentage: Math.round((available / total) * 100) };
};

initVoiceMode();
